import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, Save, Sliders } from "lucide-react";

export default function Settings() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [overheadPct, setOverheadPct] = useState(user?.overhead_pct ?? 10);
  const [profitPct, setProfitPct] = useState(user?.profit_pct ?? 10);
  const [laborRate, setLaborRate] = useState(user?.default_labor_rate ?? 65);
  const [region, setRegion] = useState(user?.region ?? "US National");
  const [saving, setSaving] = useState(false);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/auth/settings", {
        overhead_pct: parseFloat(overheadPct),
        profit_pct: parseFloat(profitPct),
        default_labor_rate: parseFloat(laborRate),
        region,
      });
      await refresh();
      toast.success("Settings saved");
    } catch (err) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppShell>
      <div className="max-w-2xl" data-testid="settings-page">
        <button
          onClick={() => navigate("/dashboard")}
          className="text-sm text-slate-400 hover:text-white flex items-center gap-1 mb-6 transition-colors"
          data-testid="back-to-dashboard-btn"
        >
          <ArrowLeft className="h-4 w-4" /> Back to projects
        </button>

        <div className="flex items-center gap-3 mb-2">
          <Sliders className="h-5 w-5 text-[#FF6A00]" />
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#FF6A00] font-semibold">
            Estimator settings
          </div>
        </div>
        <h1
          className="text-3xl md:text-4xl font-black tracking-tight text-white mb-2"
          style={{ fontFamily: "Chivo, sans-serif" }}
          data-testid="settings-heading"
        >
          Your default markups
        </h1>
        <p className="text-slate-400 mb-10">
          These get applied to every new AI estimate. You can override per project later.
        </p>

        <form onSubmit={handleSave} className="space-y-6" data-testid="settings-form">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <Label htmlFor="overhead" className="text-xs tracking-[0.2em] uppercase text-slate-400 font-semibold">
                Overhead %
              </Label>
              <Input
                id="overhead"
                type="number"
                step="0.5"
                min="0"
                max="100"
                value={overheadPct}
                onChange={(e) => setOverheadPct(e.target.value)}
                className="mt-2 bg-[#171A20] border-[#2A2F38] text-white focus:border-[#FF6A00]"
                style={{ fontFamily: "JetBrains Mono, monospace" }}
                data-testid="overhead-input"
              />
            </div>
            <div>
              <Label htmlFor="profit" className="text-xs tracking-[0.2em] uppercase text-slate-400 font-semibold">
                Profit %
              </Label>
              <Input
                id="profit"
                type="number"
                step="0.5"
                min="0"
                max="100"
                value={profitPct}
                onChange={(e) => setProfitPct(e.target.value)}
                className="mt-2 bg-[#171A20] border-[#2A2F38] text-white focus:border-[#FF6A00]"
                style={{ fontFamily: "JetBrains Mono, monospace" }}
                data-testid="profit-input"
              />
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <Label htmlFor="labor" className="text-xs tracking-[0.2em] uppercase text-slate-400 font-semibold">
                Default labor rate (USD/hr)
              </Label>
              <Input
                id="labor"
                type="number"
                step="1"
                min="0"
                value={laborRate}
                onChange={(e) => setLaborRate(e.target.value)}
                className="mt-2 bg-[#171A20] border-[#2A2F38] text-white focus:border-[#FF6A00]"
                style={{ fontFamily: "JetBrains Mono, monospace" }}
                data-testid="labor-rate-input"
              />
            </div>
            <div>
              <Label htmlFor="region" className="text-xs tracking-[0.2em] uppercase text-slate-400 font-semibold">
                Region / Market
              </Label>
              <Input
                id="region"
                value={region}
                onChange={(e) => setRegion(e.target.value)}
                placeholder="e.g. California Bay Area"
                className="mt-2 bg-[#171A20] border-[#2A2F38] text-white focus:border-[#FF6A00]"
                data-testid="region-input"
              />
            </div>
          </div>

          <div className="border-t border-[#2A2F38] pt-6">
            <Button
              type="submit"
              disabled={saving}
              className="bg-[#FF6A00] hover:bg-[#E65C00] text-white"
              data-testid="save-settings-btn"
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? "Saving…" : "Save settings"}
            </Button>
          </div>
        </form>
      </div>
    </AppShell>
  );
}
